import { Device, Document, OrgScopedBaseUser } from "@models";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { FunctionalGroup } from "@types";
import { fillRouteWithSlugs } from "@utils";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { useBulkAssignTaskPrograms } from "src/hooks/queries/task-program-mutations";
import {
  TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS,
  TaskProgram,
  TaskProgramDueAfterDays,
  TrainingCompletionMethod,
} from "src/services/backend";
import {
  DUE_AFTER_DAYS_LABEL,
  SELECTED_AUTOCOMPLETE_SX,
  TaskProgramAssignmentFields,
  TaskProgramAssignmentValues,
} from "./TaskProgramFormFields";

type FormState = TaskProgramAssignmentValues & {
  documentIds: string[];
  dueAfterDays: TaskProgramDueAfterDays | null;
  completionMethod: TrainingCompletionMethod;
};

const emptyForm: FormState = {
  documentIds: [],
  functionalGroupIds: [],
  userIds: [],
  dueAfterDays: null,
  completionMethod: "ACKNOWLEDGEMENT",
};

type Props = {
  onCancel: () => void;
  onSubmitted: () => void;
  onQuizProgramsAssigned?: (programs: TaskProgram[]) => void;
  orgId: string;
  functionalGroups: FunctionalGroup[];
  documents: Document[];
  documentsLoading: boolean;
  devices: Device[];
  devicesLoading: boolean;
  orgUsers: OrgScopedBaseUser[];
  usersLoading: boolean;
  programs?: TaskProgram[];
};

export const BulkTaskProgramAssignmentForm = ({
  onCancel,
  onSubmitted,
  onQuizProgramsAssigned,
  orgId,
  functionalGroups,
  documents,
  documentsLoading,
  devices,
  devicesLoading,
  orgUsers,
  usersLoading,
  programs = [],
}: Props) => {
  const bulkAssignMutation = useBulkAssignTaskPrograms({ orgId });

  const [form, setForm] = useState<FormState>(emptyForm);
  const [documentSearch, setDocumentSearch] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const selectedDocuments = useMemo(() => {
    const selectedIds = new Set(form.documentIds);
    return documents.filter((document) => selectedIds.has(document.id));
  }, [documents, form.documentIds]);

  const deviceNameById = useMemo(
    () => new Map(devices.map((device) => [device.id, device.name])),
    [devices]
  );

  const programByDocumentId = useMemo(
    () =>
      new Map(programs.map((program) => [program.linkedDocumentId, program])),
    [programs]
  );

  const documentsWithExistingDeadline = useMemo(() => {
    if (form.dueAfterDays == null) return [];
    return selectedDocuments
      .map((document) => {
        const program = programByDocumentId.get(document.id);
        if (!program) return null;
        if (program.dueAfterDays == null) return null;
        if (program.dueAfterDays === form.dueAfterDays) return null;
        return { document, program };
      })
      .filter(
        (entry): entry is { document: Document; program: TaskProgram } =>
          entry !== null
      );
  }, [selectedDocuments, programByDocumentId, form.dueAfterDays]);

  const formatDocumentLabel = (document: (typeof documents)[number]) => {
    if (!document.deviceId) {
      return document.title;
    }

    const deviceName = deviceNameById.get(document.deviceId);
    return deviceName ? `${document.title} (${deviceName})` : document.title;
  };

  const filteredDocuments = useMemo(() => {
    const query = documentSearch.trim().toLowerCase();

    if (!query) {
      return documents;
    }

    return documents.filter((document) =>
      document.title.toLowerCase().includes(query)
    );
  }, [documents, documentSearch]);

  const hasTrainingTargets =
    form.functionalGroupIds.length > 0 || form.userIds.length > 0;
  const canSubmit =
    form.documentIds.length > 0 &&
    hasTrainingTargets &&
    form.dueAfterDays != null;

  const handleSubmit = async () => {
    setFormError(null);
    if (form.documentIds.length === 0) {
      setFormError("Select at least one document.");
      return;
    }
    if (!hasTrainingTargets) {
      setFormError("Assign at least one functional group or user.");
      return;
    }
    if (form.dueAfterDays == null) {
      setFormError("Select a time to complete training.");
      return;
    }

    const result = await bulkAssignMutation.mutateAsync({
      documentIds: form.documentIds,
      functionalGroupIds: form.functionalGroupIds,
      userIds: form.userIds,
      dueAfterDays: form.dueAfterDays,
      ...(form.completionMethod === "QUIZ" && { completionMethod: "QUIZ" }),
    });
    if (form.completionMethod === "QUIZ") {
      if (result.programs.length > 0) {
        onQuizProgramsAssigned?.(result.programs);
      }
    }
    onSubmitted();
  };

  return (
    <Box aria-label="Training assignment form" className="flex flex-col gap-6">
      <Typography variant="body2" color="text.secondary">
        Assign trainees to documents for training. Read-and-sign tasks are sent
        immediately when eligible. Quiz tasks are sent after the quiz for that
        revision is published.
      </Typography>

      <Box>
        <TextField
          select
          fullWidth
          label="Completion method"
          value={form.completionMethod}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              completionMethod: event.target.value as TrainingCompletionMethod,
            }))
          }
        >
          <MenuItem value="ACKNOWLEDGEMENT">Read and sign</MenuItem>
          <MenuItem value="QUIZ">Quiz and sign</MenuItem>
        </TextField>
        {form.completionMethod === "QUIZ" && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            After assignment, the Agent will open to draft a quiz for each
            current document revision. Training tasks will wait until an
            administrator publishes each quiz.
          </Typography>
        )}
      </Box>

      <TaskProgramAssignmentFields
        values={form}
        onChange={(assignments) =>
          setForm((current) => ({ ...current, ...assignments }))
        }
        functionalGroups={functionalGroups}
        orgUsers={orgUsers}
        usersLoading={usersLoading}
      />

      <Box className="flex flex-col gap-3">
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Documents
        </Typography>
        <Autocomplete
          multiple
          options={documents}
          filterOptions={() => filteredDocuments}
          getOptionKey={(document) => document.id}
          getOptionLabel={formatDocumentLabel}
          value={selectedDocuments}
          inputValue={documentSearch}
          onInputChange={(_, value) => setDocumentSearch(value)}
          loading={documentsLoading || devicesLoading}
          onChange={(_, value) =>
            setForm((current) => ({
              ...current,
              documentIds: value.map((document) => document.id),
            }))
          }
          renderInput={(params) => (
            <TextField {...params} label="Documents to train on" required />
          )}
          sx={
            form.documentIds.length > 0 ? SELECTED_AUTOCOMPLETE_SX : undefined
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Box>

      <Box className="flex flex-col gap-3">
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Time to complete training
        </Typography>
        <Autocomplete<TaskProgramDueAfterDays>
          options={[...TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS]}
          getOptionLabel={(days) => DUE_AFTER_DAYS_LABEL[days]}
          value={form.dueAfterDays}
          onChange={(_, value) =>
            setForm((current) => ({
              ...current,
              dueAfterDays: value,
            }))
          }
          renderInput={(params) => (
            <TextField {...params} label="Time to complete" required />
          )}
          isOptionEqualToValue={(option, value) => option === value}
        />
        {documentsWithExistingDeadline.length > 0 && (
          <Alert severity="warning" variant="outlined">
            <AlertTitle sx={{ fontWeight: 600 }}>
              {documentsWithExistingDeadline.length === 1
                ? "1 selected document already has a deadline and will be skipped"
                : `${documentsWithExistingDeadline.length} selected documents already have deadlines and will be skipped`}
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              These trainings will keep their existing deadline. Edit each
              training individually to change it.
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {documentsWithExistingDeadline.map(({ document, program }) => (
                <li key={document.id}>
                  <Typography variant="body2" component="span">
                    <Link
                      component={RouterLink}
                      to={fillRouteWithSlugs(ROUTES.TRAINING_DETAIL, {
                        [ROUTE_SLUGS.ORG_ID]: orgId,
                        [ROUTE_SLUGS.PROGRAM_ID]: program.id,
                      })}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      color="inherit"
                      sx={{ fontWeight: 500 }}
                    >
                      {formatDocumentLabel(document)}
                    </Link>
                    <Typography
                      variant="body2"
                      component="span"
                      color="text.secondary"
                    >
                      {" "}
                      — currently {DUE_AFTER_DAYS_LABEL[program.dueAfterDays!]}
                    </Typography>
                  </Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}
      </Box>

      {formError && (
        <Typography color="error" variant="body2">
          {formError}
        </Typography>
      )}
      <Box className="flex justify-end gap-2">
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || bulkAssignMutation.isPending}
        >
          Assign
        </Button>
      </Box>
    </Box>
  );
};
